import { Button, ColorPicker, Input, Popconfirm, Select, Slider, Spin, Switch, Tooltip } from "antd";
import { LoadingOutlined } from '@ant-design/icons';
import { memo, useEffect, useRef, useState } from "react"
import styled from "styled-components";
import { UI_Header, UI_HeaderLeft, UI_HeaderLeft_TabHeader, UI_HeaderRight, UI_HeaderRight_item } from "../PanelStyle";
import { useAtom } from 'jotai';
import { showElementDetailPanelAtom } from "../../stores/mapStore";
import { FaXmark } from "react-icons/fa6";
import { IoEllipsisHorizontal } from "react-icons/io5";
import TextArea from "antd/es/input/TextArea";
import { elementsAtom, selectedElementAtom } from "../../Element/ElementStore";
import FormItemWrapper, { FormItem_Styles } from "~/components/common/FormItemWrapper/FormItemWrapper";
import { ElementAPIs } from "~/common/typescript/apis/ElementAPIs";
import IconSelector from "~/components/common/IconSelector/IconSelector";
import Tippy from '@tippyjs/react/headless';
import { PiTrash, PiPencilSimpleBold } from "react-icons/pi";
import { Element_LineString_StyleMetadata, Element_LineString_StyleMetadata_ModelUtil, Element_Point_StyleMetadata, Element_Point_StyleMetadata_ModelUtil, Element_Polygon_StyleMetadata, Element_Polygon_StyleMetadata_ModelUtil, ElementType } from "~/common/typescript/models/Element";
import { ElementMapUtilOptimized } from "../../Element/ElementMapUtilOptimized";
import { ElementItemUtil } from "../LegendPanel/ListTab/ElementGroup/ElementItemUtil";
import { AttributesManagement } from './AttributesManagement';


enum TabEnum {
  STYLE = 'STYLE',
  ATTRIBUTES = 'ATTRIBUTES',
}
/**
 * Bảng hiển thị thông tin chi tiết của đối tượng
 */
export const ElementDetailPanel = () => {
  const [showElementDetailPanel, setShowElementDetailPanel] = useAtom(showElementDetailPanelAtom);
  const [selectedElement, setSelectedElement] = useAtom(selectedElementAtom);
  const [elements, setElements] = useAtom(elementsAtom);

  const [currentTab, setCurrentTab] = useState<TabEnum>(TabEnum.STYLE);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [showLabel, setShowLabel] = useState(false);
  const [color, setColor] = useState('#ff0000');
  const [icon, setIcon] = useState('dot');
  const [size, setSize] = useState(1);
  const [frame, setFrame] = useState('circle');
  const [opacity, setOpacity] = useState(1);
  const [strokeWidth, setStrokeWidth] = useState(1);
  const [lineStyle, setLineStyle] = useState('solid');

  const [strokeColor, setStrokeColor] = useState('#ff0000');
  const [strokeOpacity, setStrokeOpacity] = useState(1);


  const tippyRef = useRef<any>(null);

  useEffect(() => {
    if (!selectedElement) return;
    setName(selectedElement.name);
    setDescription(selectedElement.description);
    {
      if (selectedElement.type == ElementType.POINT) {
        let style: Element_Point_StyleMetadata;
        {
          try {
            const metadata = JSON.parse(selectedElement.metadata!);
            style = metadata.style || {};
          } catch (error) {
            style = {};
          }
          style = Element_Point_StyleMetadata_ModelUtil.normalize(style);
          setShowLabel(style.showLabel!);
          setColor(style.color!);
          setIcon(style.icon!);
          setSize(style.size!);
          setFrame(style.frame!);
        }
      } else if (selectedElement.type == ElementType.LINESTRING) {
        let style: Element_LineString_StyleMetadata;
        {
          try {
            const metadata = JSON.parse(selectedElement.metadata!);
            style = metadata.style || {};
          } catch (error) {
            style = {};
          }
          style = Element_LineString_StyleMetadata_ModelUtil.normalize(style);
          setColor(style.color!);
          setOpacity(style.opacity!);
          setStrokeWidth(style.strokeWidth!);
          setLineStyle(style.lineStyle!);
          setShowLabel(style.showLabel!);
        }
      } else if (selectedElement.type == ElementType.POLYGON) {
        let style: Element_Polygon_StyleMetadata;
        {
          try {
            const metadata = JSON.parse(selectedElement.metadata!);
            style = metadata.style || {};
          } catch (error) {
            style = {};
          }
          style = Element_Polygon_StyleMetadata_ModelUtil.normalize(style);
          setColor(style.color!);
          setOpacity(style.opacity!);
          setStrokeWidth(style.strokeWidth!);
          setStrokeColor(style.strokeColor!);
          setStrokeOpacity(style.strokeOpacity!);
          setShowLabel(style.showLabel!);
        }
      }
    }


  }, [selectedElement]);


  // debounce update
  useEffect(() => {
    const handler = setTimeout(() => {
      doUpdateInformation();
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [name, description]);
  // end debounce update


  useEffect(() => {
    if (!selectedElement) return;

    // update metadata
    doUpdateMetadata();

    // Re-highlight element with updated style for immediate visual feedback
    setTimeout(() => {
      // Create updated element with current style for highlighting
      const updatedElement = {
        ...selectedElement,
        metadata: JSON.stringify({
          ...(selectedElement.metadata ? JSON.parse(selectedElement.metadata) : {}),
          style: selectedElement.type === ElementType.POINT ? {
            color, icon, showLabel, size, frame
          } : selectedElement.type === ElementType.LINESTRING ? {
            color, opacity, strokeWidth, lineStyle, showLabel
          } : selectedElement.type === ElementType.POLYGON ? {
            color, opacity, strokeWidth, strokeColor, strokeOpacity, showLabel
          } : {}
        })
      };
      ElementItemUtil.highlightElement(updatedElement);
    }, 50); // Smaller delay for immediate feedback
  }, [color, icon, showLabel, size, frame, opacity, strokeWidth, lineStyle, strokeColor, strokeOpacity]);

  const doUpdateMetadata = async () => {
    if (!selectedElement) return;

    // Get existing metadata first
    let existingMetadata = {};
    try {
      existingMetadata = JSON.parse(selectedElement.metadata || '{}');
    } catch (error) {
      existingMetadata = {};
    }

    let styleMetadata = {};
    if (selectedElement.type == ElementType.POINT) {
      styleMetadata = {
        color: color,
        icon: icon,
        showLabel: showLabel,
        size: size,
        frame: frame,
      }
    } else if (selectedElement.type == ElementType.LINESTRING) {
      styleMetadata = {
        color: color,
        opacity: opacity,
        strokeWidth: strokeWidth,
        lineStyle: lineStyle,
        showLabel: showLabel,
      }
    } else if (selectedElement.type == ElementType.POLYGON) {
      styleMetadata = {
        color: color,
        opacity: opacity,
        strokeWidth: strokeWidth,
        strokeColor: strokeColor,
        strokeOpacity: strokeOpacity,
        showLabel: showLabel,
      }
    }

    const metadataObject = {
      ...existingMetadata,
      style: styleMetadata
    };

    await ElementAPIs.remote_update(selectedElement.uuid, {
      metadata: metadataObject,
    });

    setElements(elements.map((element) => {
      if (element.uuid === selectedElement.uuid) {
        return {
          ...element,
          metadata: JSON.stringify(metadataObject)
        };
      }
      return element;
    }));
  }

  const doUpdateInformation = async () => {
    if (!selectedElement) return;

    await ElementAPIs.remote_update(selectedElement.uuid, {
      name: name,
      description: description,
    });

    setElements(elements.map((element) => {
      if (element.uuid === selectedElement.uuid) {
        return {
          ...element,
          name: name,
          description: description,
        };
      }
      return element;
    }));
  };

  const onClickDelete = async () => {
    if (!selectedElement) return;
    await ElementAPIs.remote_delete(selectedElement.uuid);
    setElements(elements.filter((element) => element.uuid !== selectedElement.uuid));
    ElementMapUtilOptimized.removeElementFromMap(selectedElement);
    setShowElementDetailPanel(false);
  }


  return <UI_Wrapper>
    {/* Header */}
    <UI_Header>
      <UI_HeaderLeft style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <PiPencilSimpleBold style={{ color: '#2563eb', fontSize: '18px' }} />
        <span>Chi tiết đối tượng</span>
      </UI_HeaderLeft>
      <UI_HeaderRight>
        {/* Button more */}
        <Tippy
          trigger="click"
          interactive={true}
          placement="bottom"
          offset={[0, 10]}
          delay={0}
          render={(attrs) => {
            return (
              <UI_Options_Wrapper>
                {/* Xoá */}
                <Popconfirm
                  placement="right"
                  title="Xoá đối tượng này"
                  description="Bạn có chắc chắn muốn xoá đối tượng này?"
                  onConfirm={() => {
                    onClickDelete();
                  }}
                  onCancel={() => { }}
                  okText="Xoá"
                  cancelText="Huỷ"
                >
                  <UI_Options_Button>
                    <UI_Options_ButtonIcon>
                      <PiTrash />
                    </UI_Options_ButtonIcon>

                    <span>Xóa</span>
                  </UI_Options_Button>
                </Popconfirm>
                {/* End Xoá */}
              </UI_Options_Wrapper>
            )
          }}
          onCreate={(instance) => { tippyRef.current = instance; }}
        >
          {/* <Tooltip title="Công cụ khác"> */}
          <UI_HeaderRight_item>
            <IoEllipsisHorizontal />
          </UI_HeaderRight_item>
          {/* </Tooltip> */}
        </Tippy>
        {/* End Button more */}
        <UI_HeaderRight_item onClick={() => {
          setShowElementDetailPanel(false);
        }}>
          <FaXmark />
        </UI_HeaderRight_item>
      </UI_HeaderRight>
    </UI_Header>
    {/* End Header */}
    {/* Content */}

    <UI_Content>
      {/* <FormItemWrapper label="ID">
        <Input placeholder="ID" value={selectedElement?.uuid} disabled />
      </FormItemWrapper> */}
      <FormItemWrapper label="Điền tên" hideLabel={true}>
        <Input variant="borderless" placeholder="Điền tên" value={name} onChange={(e) => setName(e.target.value)} />
      </FormItemWrapper>
      <FormItemWrapper label="Điền mô tả" hideLabel={true}>
        <TextArea variant="borderless" placeholder="Điền mô tả" value={description} onChange={(e) => setDescription(e.target.value)} autoSize={{ minRows: 1, maxRows: 2 }} />
      </FormItemWrapper>
    </UI_Content>

    <UI_Header>
      <UI_HeaderLeft>
        <UI_HeaderLeft_TabHeader onClick={() => setCurrentTab(TabEnum.STYLE)} className={currentTab === TabEnum.STYLE ? 'active' : ''}>Style</UI_HeaderLeft_TabHeader>
        <UI_HeaderLeft_TabHeader onClick={() => setCurrentTab(TabEnum.ATTRIBUTES)} className={currentTab === TabEnum.ATTRIBUTES ? 'active' : ''}>Thuộc tính</UI_HeaderLeft_TabHeader>
      </UI_HeaderLeft>
    </UI_Header>
    <UI_Content style={{ padding: '8px' }}>
      {/* Tab Style */}
      <div style={{ display: currentTab === TabEnum.STYLE ? 'block' : 'none' }}>
        {selectedElement && selectedElement.type === ElementType.POINT && <div>
          <FormItemWrapper label="Khung" inline={true}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
              <Button type={frame === 'circle' ? 'primary' : 'default'} onClick={() => {
                setFrame('circle');
              }}>Tròn</Button>
              <Button type={frame === 'square' ? 'primary' : 'default'} onClick={() => {
                setFrame('square');
              }}>Vuông</Button>
              <Button type={frame === 'none' ? 'primary' : 'default'} onClick={() => {
                setFrame('none');
              }}>Không</Button>
            </div>
          </FormItemWrapper>
          <FormItemWrapper label="Biểu tượng" inline={true} labelWidth={100}>
            <IconSelector color={color} value={icon} onChange={(icon) => {
              setIcon(icon);
            }} />
          </FormItemWrapper>
          <FormItemWrapper label="Màu" inline={true} labelWidth={100}>
            <ColorPicker size="small" showText value={color} onChange={(color) => {
              setColor(color.toHexString());
            }} />
          </FormItemWrapper>
          <FormItemWrapper label="Cỡ" inline={true} labelWidth={100}>
            <Slider value={size} onChange={(value) => {
              setSize(value);
            }} min={0.8} max={5} step={0.2} />
          </FormItemWrapper>
          <FormItemWrapper label="Nhãn" inline={true} labelWidth={100}>
            <Switch size="small" checked={showLabel} onChange={(checked) => {
              setShowLabel(checked);
            }} />
          </FormItemWrapper>
        </div>
        }

        {selectedElement && selectedElement.type === ElementType.LINESTRING && <div>
          <FormItemWrapper label="Màu" inline={true}>
            <ColorPicker size="small" showText value={color} onChange={(color) => {
              setColor(color.toHexString());
            }} />
          </FormItemWrapper>
          <FormItemWrapper label="Mờ đục" inline={true}>
            <Slider value={opacity} onChange={(value) => {
              setOpacity(value);
            }} min={0} max={1} step={0.05} />
          </FormItemWrapper>
          <FormItemWrapper label="Độ dày" inline={true}>
            <Slider value={strokeWidth} onChange={(value) => {
              setStrokeWidth(value);
            }} min={1} max={5} step={1} />
          </FormItemWrapper>
          <FormItemWrapper label="lineStyle" inline={true}>
            <Select
              value={lineStyle}
              onChange={(value) => {
                setLineStyle(value);
              }}
              options={[
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
                { value: 'dotted', label: 'Dotted' },
              ]}
            />
          </FormItemWrapper>
          <FormItemWrapper label="Nhãn" inline={true}>
            <Switch size="small" checked={showLabel} onChange={(checked) => {
              setShowLabel(checked);
            }} />
          </FormItemWrapper>
        </div>
        }
        {selectedElement && selectedElement.type === ElementType.POLYGON && <div>
          <FormItemWrapper label="Màu" inline={true}>
            <ColorPicker size="small" showText value={color} onChange={(color) => {
              setColor(color.toHexString());
            }} />
          </FormItemWrapper>
          <FormItemWrapper label="Mờ đục" inline={true}>
            <Slider value={opacity} onChange={(value) => {
              setOpacity(value);
            }} min={0} max={1} step={0.05} />
          </FormItemWrapper>
          <FormItemWrapper label="Độ dày" inline={true}>
            <Slider value={strokeWidth} onChange={(value) => {
              setStrokeWidth(value);
            }} min={1} max={5} step={1} />
          </FormItemWrapper>
          <FormItemWrapper label="Màu viền" inline={true}>
            <ColorPicker size="small" showText value={strokeColor} onChange={(color) => {
              setStrokeColor(color.toHexString());
            }} />
          </FormItemWrapper>
          <FormItemWrapper label="Mờ viền" inline={true}>
            <Slider value={strokeOpacity} onChange={(value) => {
              setStrokeOpacity(value);
            }} min={0} max={1} step={0.05} />
          </FormItemWrapper>
        </div>
        }
      </div>
      {/* End Tab Style */}
      {/* Tab Attributes */}
      <div style={{ display: currentTab === TabEnum.ATTRIBUTES ? 'block' : 'none' }}>
        {selectedElement && <AttributesManagement selectedElement={selectedElement} />}
      </div>
      {/* End tab attributes */}

    </UI_Content>
  </UI_Wrapper>
}

export default memo(ElementDetailPanel);

const UI_Wrapper = styled.div`
  width: 300px;
  height: 500px;
  position: absolute;
  top: 16px;
  right: 16px;
  border-radius: 8px;
  background-color: rgba(255,255,255, 0.8);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadows-paneShadow);
`;

const UI_Content = styled.div`
`


const UI_Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const UI_ToolbarItem = styled.div`
  cursor: pointer;
  padding: 4px;
`

const UI_Toolbar_Left = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`

const UI_Toolbar_Right = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`


const UI_Options_Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  padding: 5px 0;
  min-width: 100px;
`

const UI_Options_Button = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: 0.3s;
  font-size: 14px;
  &:hover {
    background-color: var(--color-primary);
    color: white;
  }
  &.active {
    background-color: var(--color-primary);
    color: white;
  }
`
const UI_Options_ButtonIcon = styled.div`
  width: 24px;
`